/**
 * NAMED FAILURE STATE 4 — A GOOGLE OUTAGE. 503 WITH `Retry-After`, AND NEVER 401.
 *
 * THIS SCREEN EXISTS TO PREVENT A SPECIFIC DEFECT. adr/0010-firebase-integration.md rules that
 * the guard answers "503 for `cannot verify right now` and 401 only for `this token is invalid`, so
 * the apps retry instead of signing people out", and the 503 response component says why in its own
 * words: "The two are deliberately different answers so that a Google outage does not sign every
 * author out of the cabinet" (docs/design/07-api/authors-api.yaml). A 503 that logs the
 * author out is the bug. With ~530 authors, one Google incident would become 530 support cases.
 *
 * SO THIS PANEL SAYS THE TWO THINGS THE PERSON NEEDS: it is not your account, and YOU HAVE NOT BEEN
 * SIGNED OUT. The rule itself lives in ../api/failure.ts and ../session/session-state.ts, where it
 * is tested; this component is what the rule looks like.
 *
 * THE COUNTDOWN IS AN INTERVAL, NOT TWO INSTANTS SUBTRACTED. C11 bans reading a clock outside the
 * injected one, and counting down is also the more honest implementation — it stays correct if the
 * machine's clock is adjusted mid-outage. `Retry-After` is declared as an INTEGER NUMBER OF
 * SECONDS on this surface (authors-api.yaml), and it is the only response component in
 * the whole of authors-api that carries the header at all.
 */
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from '../../../i18n';
import { IDENTITY_UNAVAILABLE_BODY, IDENTITY_UNAVAILABLE_TITLE } from '../copy';

export interface IdentityUnavailablePanelProps {
  /** Counts down to the automatic retry. `null` while a retry is actually in flight. */
  readonly secondsUntilRetry: number | null;
  readonly onRetryNow: () => void;
  /** True while the retry is running, so the button says so rather than looking inert. */
  readonly retrying?: boolean;
  /** Shown when there is still a last-good session behind this panel. */
  readonly sessionHeld?: boolean;
}

export function IdentityUnavailablePanel({
  secondsUntilRetry,
  onRetryNow,
  retrying = false,
  sessionHeld = false,
}: IdentityUnavailablePanelProps) {
  const { t } = useTranslation('auth');
  return (
    <Alert
      // `alert` rather than `status`: this interrupts what the person was doing, and they need to
      // know why the screen stopped responding to them.
      role="alert"
      variant="light"
      color="yellow"
      title={t(IDENTITY_UNAVAILABLE_TITLE)}
    >
      <Stack gap="sm">
        <Text size="sm">{t(IDENTITY_UNAVAILABLE_BODY)}</Text>

        {sessionHeld ? (
          <Text size="sm" c="dimmed">
            {t('identityUnavailable.sessionHeld')}
          </Text>
        ) : null}

        <Group gap="sm" align="center">
          <Button variant="filled" size="sm" onClick={onRetryNow} loading={retrying}>
            {t('identityUnavailable.tryNow')}
          </Button>
          {/*
            Polite, not assertive: a ticking number must not interrupt a screen reader every second.
            The visible text and the announced text are the same string, so nothing is hidden from
            anybody.
          */}
          <Text size="sm" c="dimmed" role="status" aria-live="polite">
            {retrying
              ? t('identityUnavailable.checkingAgain')
              : secondsUntilRetry === null
                ? t('identityUnavailable.retryingShortly')
                : t('identityUnavailable.retryingIn', { seconds: secondsUntilRetry })}
          </Text>
        </Group>
      </Stack>
    </Alert>
  );
}
